import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, X, Phone, Mail, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { EquipmentCard } from '../components/EquipmentCard';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedLocationEmail } from '../components/ObfuscatedLocationEmail';
import { GoogleBusinessSection } from '../components/GoogleBusiness';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Equipment Rental Logo
const EQUIPMENT_LOGO = "https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/gjop30ki_Logo%20001%20bw%20-%20Kenscho%20Transport.png";

// Predefined categories
const EQUIPMENT_CATEGORIES = [
  'Concrete',
  'Demolition',
  'Construction',
  'Shoring',
  'Heavy Equipment',
  'Aerial Equipment',
  'Transport',
  'Power and Lighting',
  'Landscaping',
  'General Tools'
];

const CATEGORY_TRANSLATIONS = {
  'Concrete': 'Béton',
  'Demolition': 'Démolition',
  'Construction': 'Construction',
  'Shoring': 'Étaiement',
  'Heavy Equipment': 'Équipement lourd',
  'Aerial Equipment': 'Équipement aérien',
  'Transport': 'Transport',
  'Power and Lighting': 'Énergie et éclairage',
  'Landscaping': 'Aménagement paysager',
  'General Tools': 'Outils généraux'
};

// Local Business Structured Data for SEO
const structuredData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Kenscho Equipment Rental",
  "alternateName": "Equipment Kenscho Rental",
  "description": "Kenscho Equipment Rental - Professional tool and equipment rental services in Lanaudière, Laurentians, Laval, and Montreal. Concrete tools, demolition equipment, construction machinery, and more from Equipment Kenscho Rental.",
  "url": "https://www.kenscho.ca/equipment-rental",
  "telephone": "+1-514-XXX-XXXX",
  "email": "info@kenscho.ca",
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "Quebec",
    "addressCountry": "CA"
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "Lanaudière, Quebec"
    },
    {
      "@type": "Place", 
      "name": "Laurentians, Quebec"
    },
    {
      "@type": "Place",
      "name": "Laval, Quebec"
    },
    {
      "@type": "Place",
      "name": "Montreal, Quebec"
    }
  ],
  "serviceType": [
    "Kenscho Equipment Rental",
    "Equipment Kenscho Rental",
    "Tool Rental",
    "Equipment Rental",
    "Construction Equipment Rental",
    "Concrete Tool Rental",
    "Demolition Equipment Rental"
  ],
  "priceRange": "$$"
};

export default function EquipmentRental() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { t, language, getLocalizedLink } = useLanguage();

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await axios.get(`${API}/equipment/rental`);
        setEquipment(response.data);
      } catch (error) {
        console.error('Error fetching equipment:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipment();
  }, []);

  // Filter equipment based on search and category
  const filteredEquipment = useMemo(() => {
    return equipment.filter(item => {
      // Search filter - check name, description, category, and categories array
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        item.name?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower) ||
        item.categories?.some(cat => cat.toLowerCase().includes(searchLower)) ||
        (language === 'fr' && (
          item.name_fr?.toLowerCase().includes(searchLower) ||
          item.description_fr?.toLowerCase().includes(searchLower) ||
          item.category_fr?.toLowerCase().includes(searchLower)
        ));
      
      // Category filter - check both legacy category and new categories array
      const matchesCategory = !selectedCategory || 
        item.category === selectedCategory ||
        item.categories?.includes(selectedCategory);
      
      return matchesSearch && matchesCategory;
    });
  }, [equipment, searchQuery, selectedCategory, language]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
  };

  const getCategoryLabel = (category) => {
    if (language === 'fr' && CATEGORY_TRANSLATIONS[category]) {
      return CATEGORY_TRANSLATIONS[category];
    }
    return category;
  };

  return (
    <div className="grain-texture pt-20" data-testid="equipment-rental-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{language === 'fr' 
          ? 'Location Kenscho | Location d\'outils et équipement | Lanaudière, Laurentides, Laval, Montréal'
          : 'Kenscho Equipment Rental | Tool & Equipment Rental | Lanaudière, Laurentians, Laval, Montreal'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Location Kenscho - Louez des outils et équipements de construction professionnels à Lanaudière, Laurentides, Laval et Montréal. Mélangeurs à béton, outils de démolition, équipement électrique. Tarifs journaliers, hebdomadaires et mensuels chez Location Kenscho.'
          : 'Kenscho Equipment Rental - Rent professional tools and construction equipment in Lanaudière, Laurentians, Laval, and Montreal. Concrete mixers, demolition tools, power equipment. Daily, weekly, and monthly rates at Equipment Kenscho Rental.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'Location Kenscho, location outils, location équipement, Lanaudière, Laurentides, Laval, Montréal, béton, démolition, construction, outils électriques, louer outils'
          : 'Kenscho Equipment Rental, Equipment Kenscho Rental, tool rental, equipment rental, Lanaudière, Laurentians, Laval, Montreal, concrete tools, demolition equipment, construction rental, power tools rental'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/equipment-rental')}`} />
        
        {/* Hreflang Tags for SEO */}
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/equipment-rental" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/equipment-rental" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/equipment-rental" />
        
        {/* Open Graph */}
        <meta property="og:title" content={language === 'fr' ? 'Location Kenscho | Location d\'outils et équipement' : 'Kenscho Equipment Rental | Tool & Equipment Rental'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Location Kenscho - Location d\'outils et équipements professionnels à Lanaudière, Laurentides, Laval et Montréal. Tarifs journaliers, hebdomadaires et mensuels compétitifs.'
          : 'Kenscho Equipment Rental - Professional tool and equipment rental in Lanaudière, Laurentians, Laval & Montreal. Competitive daily, weekly, and monthly rates from Equipment Kenscho Rental.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/equipment-rental')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:locale:alternate" content={language === 'fr' ? 'en_CA' : 'fr_CA'} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      {/* Hero */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 animate-fade-in-up">
            <img 
              src={EQUIPMENT_LOGO} 
              alt={language === 'fr' ? 'Location Kenscho' : 'Kenscho Equipment Rental'} 
              className="h-20 md:h-24 w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t('equipment.title1')} <span className="text-primary">{t('equipment.title2')}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t('equipment.desc')}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">
                  {language === 'fr' ? 'Prêt à commencer?' : 'Ready to get started?'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? 'Appelez maintenant pour réserver votre équipement ou obtenir un devis.'
                    : 'Call now to reserve your equipment or get a quote for your rental needs.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedLocationEmail className="font-medium" />
                  </div>
                </div>
                <Button asChild className="rounded-full btn-animate">
                  <Link to={getLocalizedLink('/contact')}>
                    {language === 'fr' ? 'Obtenir un devis' : 'Get a Quote'} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serving Areas */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-muted/50 border border-border/40 rounded-xl p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{language === 'fr' ? 'Nous desservons' : 'Serving'}</span>
              </div>
              {(language === 'fr' 
                ? ['Lanaudière', 'Laurentides', 'Laval', 'Montréal']
                : ['Lanaudière', 'Laurentians', 'Laval', 'Montreal']
              ).map((area) => (
                <span key={area} className="text-sm px-4 py-1.5 bg-background border border-border/60 rounded-full">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={language === 'fr' ? 'Rechercher par nom, description...' : 'Search by name, description...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 rounded-full"
                data-testid="equipment-search"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category Filters */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  !selectedCategory 
                    ? 'bg-primary text-white' 
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
                data-testid="category-all"
              >
                {language === 'fr' ? 'Tous' : 'All'}
              </button>
              {EQUIPMENT_CATEGORIES.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    selectedCategory === category 
                      ? 'bg-primary text-white' 
                      : 'bg-muted hover:bg-muted/80 text-foreground'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {getCategoryLabel(category)}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters indicator */}
          {(searchQuery || selectedCategory) && (
            <div className="mb-6 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {filteredEquipment.length} {language === 'fr' ? 'résultat(s)' : 'result(s)'}
              </span>
              <button 
                onClick={clearFilters}
                className="text-sm text-primary hover:underline"
              >
                {language === 'fr' ? 'Effacer les filtres' : 'Clear filters'}
              </button>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="text-3xl font-light tracking-tight">
                {t('equipment.available')}
              </h2>
              <p className="text-muted-foreground mt-2">
                {t('equipment.browseSelection')}
              </p>
            </div>
            <Button asChild variant="outline" className="rounded-full">
              <Link to={getLocalizedLink('/contact')}>
                {t('equipment.customRequest')} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-muted animate-pulse h-96 rounded-lg" />
              ))}
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                {language === 'fr' 
                  ? 'Aucun équipement trouvé. Essayez une autre recherche.' 
                  : 'No equipment found. Try a different search.'}
              </p>
              <Button onClick={clearFilters} variant="outline" className="mt-4 rounded-full">
                {language === 'fr' ? 'Effacer les filtres' : 'Clear filters'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEquipment.map((item) => (
                <div key={item.id} className="stagger-item">
                  <EquipmentCard equipment={item} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Rental Info */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-light tracking-tight mb-8">
                {t('equipment.rentalInfo')}
              </h2>
              <div className="space-y-6">
                {t('equipment.rentalPoints').map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-primary font-medium">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border/60 rounded-lg p-8">
              <h3 className="text-xl font-medium mb-6">{t('equipment.howToRent')}</h3>
              <ol className="space-y-4">
                {t('equipment.steps').map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm shrink-0">{index + 1}</span>
                    <p className="text-muted-foreground">{step}</p>
                  </li>
                ))}
              </ol>
              <Button asChild className="w-full rounded-full mt-8 btn-animate" data-testid="rental-cta-btn">
                <Link to={getLocalizedLink('/contact')}>
                  {t('equipment.inquireAbout')} <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Google Business Section - Location Kenscho */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <GoogleBusinessSection profile="equipment" showMap={true} />
        </div>
      </section>
    </div>
  );
}
